import React, { useState, useEffect, useRef, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { useGoogleMap, Polyline, Polygon, Marker } from '@react-google-maps/api'

const DEFAULT_POLYGON_OPTIONS = {
  fillColor: '#7ac8ed',
  fillOpacity: 0.4,
  strokeColor: '#7ac8ed',
  strokeOpacity: 0.8,
  strokeWeight: 1,
}

// Rendered as a child of <GoogleMap> (via @react-google-maps/api).
// Attaches click/dblclick listeners to the map and shows an in-progress
// polygon preview. Calls onComplete([{lat, lng}, ...]) when the user
// closes the polygon (by clicking the first vertex, pressing "Done", or
// double-clicking the map).
function PolygonDrawing({ polygonOptions, onComplete, onCancel }) {
  const map = useGoogleMap()

  // Use a ref for synchronous access inside async map event handlers, and
  // mirror it into state so React re-renders the preview overlays.
  const verticesRef = useRef([])
  const [renderVertices, setRenderVertices] = useState([])
  const [controlContainer, setControlContainer] = useState(null)

  // Keep a stable ref to onComplete so the map event listener closure
  // always calls the latest version without needing to re-attach.
  const onCompleteRef = useRef(onComplete)
  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  const mergedOptions = { ...DEFAULT_POLYGON_OPTIONS, ...(polygonOptions || {}) }

  const commitCompletion = useCallback((verts) => {
    verticesRef.current = []
    setRenderVertices([])
    onCompleteRef.current(verts)
  }, [])

  // Inject a control div into the map's TOP_CENTER controls.
  useEffect(() => {
    if (!map || !window.google) {
      return undefined
    }

    const container = document.createElement('div')
    map.controls[window.google.maps.ControlPosition.TOP_CENTER].push(container)
    setControlContainer(container)
    map.setOptions({ draggableCursor: 'crosshair' })

    return () => {
      const controls = map.controls[window.google.maps.ControlPosition.TOP_CENTER]
      for (let i = 0; i < controls.getLength(); i++) {
        if (controls.getAt(i) === container) {
          controls.removeAt(i)
          break
        }
      }
      map.setOptions({ draggableCursor: '' })
    }
  }, [map])

  // Attach click / dblclick listeners to the map.
  useEffect(() => {
    if (!map || !window.google) {
      return undefined
    }

    const clickListener = window.google.maps.event.addListener(map, 'click', (e) => {
      const newVertex = { lat: e.latLng.lat(), lng: e.latLng.lng() }
      const newVerts = [...verticesRef.current, newVertex]
      verticesRef.current = newVerts
      setRenderVertices([...newVerts])
    })

    // Google Maps fires two click events before dblclick. After dblclick the
    // verticesRef has two duplicate trailing entries — remove the last one so
    // the polygon closes cleanly at the dblclick location.
    const dblclickListener = window.google.maps.event.addListener(map, 'dblclick', () => {
      const current = verticesRef.current
      // Need at least 4 entries (3 real vertices + 1 duplicate from 2nd click).
      if (current.length >= 4) {
        commitCompletion(current.slice(0, -1))
      }
    })

    return () => {
      window.google.maps.event.removeListener(clickListener)
      window.google.maps.event.removeListener(dblclickListener)
    }
  }, [map, commitCompletion])

  const handleClosePolygon = useCallback(() => {
    const current = verticesRef.current
    if (current.length >= 3) {
      commitCompletion([...current])
    }
  }, [commitCompletion])

  const handleUndoLast = useCallback(() => {
    const newVerts = verticesRef.current.slice(0, -1)
    verticesRef.current = newVerts
    setRenderVertices([...newVerts])
  }, [])

  const handleCancel = useCallback(() => {
    verticesRef.current = []
    setRenderVertices([])
    if (onCancel) {
      onCancel()
    }
  }, [onCancel])

  const strokeColor = mergedOptions.strokeColor || '#7ac8ed'

  const firstVertexIcon = window.google?.maps?.SymbolPath
    ? {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 7,
        fillColor: strokeColor,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      }
    : null

  const toolbar = controlContainer
    ? ReactDOM.createPortal(
        <div
          style={{
            background: '#fff',
            border: '2px solid #ccc',
            borderRadius: '3px',
            boxShadow: '0 2px 6px rgba(0,0,0,.3)',
            cursor: 'default',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 10px',
            marginTop: '8px',
            fontSize: '13px',
            userSelect: 'none',
          }}
        >
          <span style={{ color: '#555' }}>
            {renderVertices.length === 0
              ? 'Click map to start drawing polygon'
              : renderVertices.length < 3
              ? `${renderVertices.length} point${renderVertices.length > 1 ? 's' : ''} — add at least ${3 - renderVertices.length} more`
              : `${renderVertices.length} points — click ● or double-click to finish`}
          </span>
          {renderVertices.length > 0 && (
            <button
              type="button"
              title="Undo last point"
              onClick={handleUndoLast}
              style={{
                background: '#fff',
                border: '1px solid #aaa',
                borderRadius: '2px',
                cursor: 'pointer',
                padding: '2px 7px',
                fontSize: '12px',
              }}
            >
              Undo
            </button>
          )}
          {renderVertices.length >= 3 && (
            <button
              type="button"
              title="Close and save polygon"
              onClick={handleClosePolygon}
              style={{
                background: '#4a90d9',
                border: 'none',
                borderRadius: '2px',
                color: '#fff',
                cursor: 'pointer',
                padding: '2px 7px',
                fontSize: '12px',
              }}
            >
              Done
            </button>
          )}
          {renderVertices.length > 0 && (
            <button
              type="button"
              title="Cancel drawing"
              onClick={handleCancel}
              style={{
                background: '#fff',
                border: '1px solid #aaa',
                borderRadius: '2px',
                cursor: 'pointer',
                padding: '2px 7px',
                fontSize: '12px',
                color: '#c00',
              }}
            >
              Cancel
            </button>
          )}
        </div>,
        controlContainer
      )
    : null

  return (
    <>
      {toolbar}
      {renderVertices.length >= 2 && (
        <Polyline
          path={renderVertices}
          options={{
            strokeColor,
            strokeOpacity: mergedOptions.strokeOpacity,
            strokeWeight: (mergedOptions.strokeWeight || 1) + 1,
            clickable: false,
          }}
        />
      )}
      {renderVertices.length >= 3 && (
        <Polygon
          path={renderVertices}
          options={{
            ...mergedOptions,
            editable: false,
            clickable: false,
          }}
        />
      )}
      {renderVertices.length >= 3 && firstVertexIcon && (
        <Marker
          position={renderVertices[0]}
          icon={firstVertexIcon}
          title="Click to complete polygon"
          cursor="pointer"
          onClick={handleClosePolygon}
        />
      )}
    </>
  )
}

export default PolygonDrawing
